import { ThemeColors } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/theme/colors'

import { Size as TextSize } from '../bodyText/BodyText'

/**
 * This file contains all the styling for our button components.
 * All low level keys should be valid ViewStyles.
 * Sizes, states and focus properties overwrite base values.
 */

export const getButtonStyles = (tokenColors: ThemeColors) => ({
  primary: {
    default: {
      base: {
        backgroundColor: tokenColors.primaryButtonBackgroundDefault,
        color: tokenColors.primaryButtonTextActive,
        minWidth: 100,
      },
      sizes: {
        default: {
          height: 48,
          paddingHorizontal: 24,
        },
        small: {
          height: 40,
          paddingHorizontal: 12,
        },
        extraSmall: {
          height: 32,
          paddingHorizontal: 12,
        },
        large: {
          paddingHorizontal: 36,
          height: 48,
        },
      },
      states: {
        hovered: {
          backgroundColor: tokenColors.primaryButtonBackgroundHover,
        },
        pressed: {
          backgroundColor: tokenColors.primaryButtonBackgroundPressed,
        },
        inactive: {
          backgroundColor: tokenColors.primaryButtonBackgroundInactive,
          color: tokenColors.primaryButtonTextInactive,
        },
        loading: {
          backgroundColor: tokenColors.primaryButtonBackgroundLoading,
        },
      },
      focused: {
        backgroundColor: tokenColors.primaryButtonBackgroundFocus,
      },
    },
    periwinkle: {
      base: {
        backgroundColor: tokenColors.primaryButtonPeriwinkleBackgroundDefault,
        color: tokenColors.primaryButtonTextActive,
        minWidth: 100,
      },
      sizes: {
        default: {
          height: 48,
          paddingHorizontal: 23,
        },
        small: {
          height: 40,
          paddingHorizontal: 15,
        },
        unsized: {},
      },
      states: {
        hovered: {
          backgroundColor: tokenColors.primaryButtonPeriwinkleBackgroundHover,
        },
        pressed: {
          backgroundColor: tokenColors.primaryButtonPeriwinkleBackgroundPressed,
        },
        inactive: {
          color: tokenColors.secondaryButtonTextInactive,
          backgroundColor: tokenColors.primaryButtonPeriwinkleBackgroundInactive,
          border: `1.5px solid ${tokenColors.secondaryButtonTextInactive}`,
        },
        loading: {
          backgroundColor: tokenColors.primaryButtonPeriwinkleBackgroundLoading,
        },
      },
      focused: {
        backgroundColor: tokenColors.primaryButtonPeriwinkleBackgroundFocus,
      },
    },
  },
  secondary: {
    default: {
      base: {
        backgroundColor: tokenColors.secondaryButtonBackgroundDefault,
        color: tokenColors.secondaryButtonTextActive,
        borderWidth: 1.5,
        borderColor: tokenColors.secondaryButtonOutlineDefault,
        minWidth: 100,
      },
      sizes: {
        default: {
          height: 48,
          paddingHorizontal: 23,
        },
        small: {
          height: 40,
          paddingHorizontal: 15,
        },
        unsized: {},
      },
      states: {
        hovered: {
          backgroundColor: tokenColors.secondaryButtonBackgroundHover,
        },
        pressed: {
          backgroundColor: tokenColors.secondaryButtonBackgroundPressed,
        },
        inactive: {
          color: tokenColors.secondaryButtonTextInactive,
          backgroundColor: tokenColors.secondaryButtonBackgroundInactive,
          border: `1.5px solid ${tokenColors.secondaryButtonOutlineInactive}`,
        },
        loading: {
          backgroundColor: tokenColors.secondaryButtonBackgroundPressed,
        },
        selected: {
          backgroundColor: tokenColors.primaryButtonBackgroundDefault,
          color: tokenColors.primaryButtonTextActive,
        },
      },
      focused: {
        backgroundColor: tokenColors.secondaryButtonBackgroundFocus,
      },
    },
    inverse: {
      base: {
        backgroundColor: tokenColors.secondaryButtonInverseBackgroundDefault,
        color: tokenColors.secondaryButtonInverseTextActive,
        borderWidth: 1.5,
        borderColor: tokenColors.secondaryButtonInverseOutlineDefault,
        minWidth: 100,
      },
      sizes: {
        default: {
          height: 48,
          paddingHorizontal: 23,
        },
        small: {
          height: 40,
          paddingHorizontal: 15,
        },
      },
      states: {
        hovered: {
          backgroundColor: tokenColors.secondaryButtonInverseBackgroundHover,
        },
        pressed: {
          backgroundColor: tokenColors.secondaryButtonInverseBackgroundPressed,
          borderColor: tokenColors.secondaryButtonInverseOutlinePressed,
        },
        inactive: {
          color: tokenColors.secondaryButtonInverseTextInactive,
          border: `1.5px solid ${tokenColors.secondaryButtonInverseOutlineInactive}`,
        },
        loading: {
          backgroundColor: tokenColors.secondaryButtonInverseBackgroundPressed,
          borderColor: tokenColors.secondaryButtonInverseOutlinePressed,
        },
      },
      focused: {
        backgroundColor: tokenColors.secondaryButtonInverseBackgroundFocus,
      },
    },
    black: {
      base: {
        color: tokenColors.secondaryButtonBlackTextActive,
        borderWidth: 1.5,
        borderColor: tokenColors.secondaryButtonBlackOutlineDefault,
        backgroundColor: tokenColors.secondaryButtonBlackBackgroundDefault,
        minWidth: 100,
      },
      sizes: {
        default: {
          height: 48,
          paddingHorizontal: 23,
        },
      },
      states: {
        hovered: {},
        pressed: {},
        inactive: {
          color: tokenColors.secondaryButtonTextInactive,
          backgroundColor: tokenColors.secondaryButtonBackgroundInactive,
          borderColor: tokenColors.secondaryButtonBackgroundInactive,
        },
        loading: {},
      },
      focused: {
        backgroundColor: tokenColors.secondaryButtonBackgroundFocus,
      },
    },
    periwinkle: {
      base: {
        color: tokenColors.secondaryButtonPeriwinkleTextActive,
        border: `1.5px solid ${tokenColors.secondaryButtonPeriwinkleOutlineDefault}`,
        minWidth: 100,
      },
      sizes: {
        default: {
          height: 48,
          paddingHorizontal: 23,
        },
        small: {
          height: 40,
          paddingHorizontal: 15,
        },
        unsized: {},
      },
      states: {
        hovered: {
          backgroundColor: tokenColors.secondaryButtonPeriwinkleBackgroundHover,
        },
        pressed: {
          backgroundColor: tokenColors.secondaryButtonPeriwinkleBackgroundPressed,
        },
        inactive: {
          color: tokenColors.secondaryButtonTextInactive,
          backgroundColor: tokenColors.secondaryButtonBackgroundInactive,
          border: `1.5px solid ${tokenColors.secondaryButtonOutlineInactive}`,
        },
        loading: {
          backgroundColor: tokenColors.secondaryButtonPeriwinkleBackgroundLoading,
        },
      },
      focused: {
        backgroundColor: tokenColors.secondaryButtonPeriwinkleBackgroundFocus,
      },
    },
    gold: {
      base: {
        color: tokenColors.secondaryButtonGoldTextActive,
        border: `1.5px solid ${tokenColors.secondaryButtonGoldOutlineDefault}`,
        minWidth: 100,
      },
      sizes: {
        default: {
          height: 48,
          paddingHorizontal: 23,
        },
        small: {
          height: 40,
          paddingHorizontal: 15,
        },
        unsized: {},
      },
      states: {
        hovered: {
          backgroundColor: tokenColors.secondaryButtonGoldBackgroundHover,
        },
        pressed: {
          backgroundColor: tokenColors.secondaryButtonGoldBackgroundPressed,
        },
        inactive: {
          color: tokenColors.secondaryButtonTextInactive,
          backgroundColor: tokenColors.secondaryButtonBackgroundInactive,
          border: `1.5px solid ${tokenColors.secondaryButtonOutlineInactive}`,
        },
        loading: {
          backgroundColor: tokenColors.secondaryButtonGoldBackgroundLoading,
        },
      },
      focused: {
        backgroundColor: tokenColors.secondaryButtonGoldBackgroundFocus,
      },
    },
    oatmeal: {
      base: {
        color: tokenColors.secondaryButtonOatmealTextActive,
        backgroundColor: tokenColors.secondaryButtonOatmealBackgroundDefault,
        borderWidth: 1.5,
        borderColor: tokenColors.secondaryButtonOatmealOutlineDefault,
        minWidth: 100,
      },
      sizes: {
        default: {
          height: 48,
          paddingHorizontal: 23,
        },
        small: {
          height: 40,
          paddingHorizontal: 15,
        },
        unsized: {},
      },
      states: {
        hovered: {
          backgroundColor: tokenColors.secondaryButtonBackgroundHover,
          color: tokenColors.secondaryButtonTextActive,
          borderColor: tokenColors.secondaryButtonOutlineDefault,
        },
        pressed: {
          backgroundColor: tokenColors.secondaryButtonBackgroundPressed,
          color: tokenColors.secondaryButtonTextActive,
          borderColor: tokenColors.secondaryButtonOutlineDefault,
        },
        inactive: {
          color: tokenColors.secondaryButtonTextInactive,
          backgroundColor: tokenColors.secondaryButtonBackgroundInactive,
          border: `1.5px solid ${tokenColors.secondaryButtonOutlineInactive}`,
        },
        loading: {
          backgroundColor: tokenColors.secondaryButtonBackgroundPressed,
        },
        selected: {
          backgroundColor: tokenColors.secondaryButtonBackgroundHover,
          color: tokenColors.secondaryButtonTextActive,
          borderColor: tokenColors.secondaryButtonOutlineDefault,
        },
      },
      focused: {
        backgroundColor: tokenColors.secondaryButtonBackgroundFocus,
      },
    },
  },
  tertiary: {
    default: {
      base: {
        backgroundColor: tokenColors.tertiaryButtonBackgroundDefault,
        color: tokenColors.tertiaryButtonTextActive,
      },
      sizes: {
        default: {
          height: 48,
          paddingHorizontal: 24,
        },
        small: {
          height: 40,
          paddingHorizontal: 16,
        },
      },
      states: {
        hovered: {
          backgroundColor: tokenColors.tertiaryButtonBackgroundHover,
        },
        pressed: {
          backgroundColor: tokenColors.tertiaryButtonBackgroundPressed,
        },
        inactive: {
          backgroundColor: tokenColors.tertiaryButtonBackgroundInactive,
          color: tokenColors.tertiaryButtonTextInactive,
        },
        loading: {
          backgroundColor: tokenColors.tertiaryButtonBackgroundPressed,
        },
      },
      focused: {
        backgroundColor: tokenColors.tertiaryButtonBackgroundFocus,
      },
    },
  },
  text: {
    default: {
      base: {
        color: tokenColors.linkDefault,
      },
      sizes: {
        default: {},
      },
      states: {
        hovered: {
          textDecorationLine: 'underline',
          backgroundColor: 'transparent',
        },
        pressed: {
          color: tokenColors.linkHover,
          textDecorationLine: 'underline',
        },
        inactive: {
          color: tokenColors.linkInactive,
          backgroundColor: tokenColors.backgroundTransparent,
        },
        loading: {},
      },
      focused: {
        backgroundColor: tokenColors.backgroundFocus,
        textDecorationLine: 'underline',
      },
    },
  },
  wordCloud: {
    default: {
      base: {
        color: tokenColors.wordCloudButtonTextActive,
        borderWidth: 1,
        borderColor: tokenColors.wordCloudButtonOutlineDefault,
        backgroundColor: tokenColors.wordCloudButtonBackgroundDefault,
      },
      sizes: {
        default: {
          height: 48,
          paddingHorizontal: 23,
        },
        small: {
          height: 45,
          paddingHorizontal: 20,
          textSize: TextSize.SMALL,
        },
        unsized: {},
      },
      states: {
        hovered: {
          borderColor: tokenColors.wordCloudButtonOutlineHover,
        },
        pressed: {
          color: tokenColors.wordCloudButtonTextPressed,
          borderColor: tokenColors.wordCloudButtonOutlinePressed,
          backgroundColor: tokenColors.wordCloudButtonBackgroundPressed,
        },
        inactive: {
          color: tokenColors.wordCloudButtonTextInactive,
          borderColor: tokenColors.wordCloudButtonOutlineInactive,
        },
        selected: {
          color: tokenColors.wordCloudButtonTextSelected,
          backgroundColor: tokenColors.wordCloudButtonBackgroundSelected,
          borderColor: tokenColors.wordCloudButtonOutlineSelected,
        },
        'selected-inactive': {
          color: tokenColors.wordCloudButtonTextInactive,
          borderColor: tokenColors.wordCloudButtonOutlineInactive,
          backgroundColor: tokenColors.wordCloudButtonBackgroundSelected,
        },
      },
    },
    periwinkle: {
      base: {
        color: tokenColors.wordCloudButtonTextActive,
        borderWidth: 1,
        borderColor: tokenColors.wordCloudButtonOutlineDefault,
        backgroundColor: tokenColors.wordCloudButtonBackgroundDefault,
      },
      sizes: {
        default: {
          height: 48,
          paddingHorizontal: 23,
        },
        small: {
          height: 45,
          paddingHorizontal: 20,
          textSize: TextSize.SMALL,
        },
      },
      states: {
        hovered: {
          borderColor: tokenColors.wordCloudPeriwinkleButtonOutlineHover,
        },
        pressed: {
          color: tokenColors.wordCloudPeriwinkleButtonTextPressed,
          borderColor: tokenColors.wordCloudPeriwinkleButtonOutlinePressed,
        },
        inactive: {
          color: tokenColors.secondaryButtonTextInactive,
          borderColor: tokenColors.secondaryButtonBackgroundInactive,
        },
        selected: {
          color: tokenColors.wordCloudPeriwinkleButtonTextSelected,
          backgroundColor: tokenColors.wordCloudPeriwinkleButtonBackgroundSelected,
          borderColor: tokenColors.wordCloudPeriwinkleButtonOutlineSelected,
        },
        'selected-inactive': {
          color: tokenColors.wordCloudButtonTextInactive,
          borderColor: tokenColors.wordCloudButtonOutlineInactive,
          backgroundColor: tokenColors.wordCloudButtonBackgroundSelected,
        },
      },
      focused: {
        backgroundColor: tokenColors.secondaryButtonPeriwinkleBackgroundFocus,
      },
    },
  },
  icon: {
    default: {
      base: {
        backgroundColor: tokenColors.backgroundTransparent,
        color: tokenColors.tertiaryButtonTextActive,
      },
      sizes: {
        default: {
          padding: 14,
        },
        small: {
          padding: 3,
        },
      },
      states: {
        hovered: {
          backgroundColor: tokenColors.tertiaryButtonBackgroundHover,
        },
        pressed: {
          backgroundColor: tokenColors.tertiaryButtonBackgroundPressed,
        },
        inactive: {
          backgroundColor: tokenColors.tertiaryButtonBackgroundInactive,
        },
        selected: {
          backgroundColor: tokenColors.tertiaryButtonBackgroundHover,
        },
      },
      focused: {
        backgroundColor: tokenColors.tertiaryButtonBackgroundFocus,
      },
    },
    inverse: {
      base: {
        backgroundColor: tokenColors.secondaryButtonInverseBackgroundDefault,
        color: tokenColors.secondaryButtonInverseTextActive,
        borderWidth: 1.5,
        borderColor: tokenColors.secondaryButtonInverseOutlineDefault,
      },
      sizes: {
        default: {
          padding: 14,
        },
        small: {
          padding: 3,
        },
      },
      states: {
        hovered: {
          backgroundColor: tokenColors.secondaryButtonInverseBackgroundHover,
        },
        pressed: {
          backgroundColor: tokenColors.secondaryButtonInverseBackgroundPressed,
          borderColor: tokenColors.secondaryButtonInverseOutlinePressed,
        },
        inactive: {
          color: tokenColors.secondaryButtonInverseTextInactive,
          border: `1.5px solid ${tokenColors.secondaryButtonInverseOutlineInactive}`,
        },
        loading: {
          backgroundColor: tokenColors.secondaryButtonInverseBackgroundPressed,
          borderColor: tokenColors.secondaryButtonInverseOutlinePressed,
        },
      },
      focused: {
        backgroundColor: tokenColors.secondaryButtonBackgroundFocus,
      },
    },
    unstyled: {
      base: {
        height: 24,
        width: 24,
      },
      sizes: {
        default: {},
      },
      states: {},
    },
  },
  primary_icon: {
    default: {
      base: {
        backgroundColor: tokenColors.primaryIconButtonBackgroundDefault,
        color: tokenColors.tertiaryButtonTextActive,
      },
      sizes: {
        default: {
          height: 40,
          width: 40,
        },
      },
      states: {
        hovered: {
          backgroundColor: tokenColors.primaryIconButtonBackgroundHover,
        },
        pressed: {
          backgroundColor: tokenColors.primaryIconButtonBackgroundPressed,
        },
        inactive: {
          backgroundColor: tokenColors.primaryIconButtonBackgroundInactive,
        },
        selected: {
          backgroundColor: tokenColors.primaryIconButtonBackgroundHover,
        },
      },
      focused: {
        backgroundColor: tokenColors.primaryIconButtonBackgroundFocus,
      },
    },
  },
  secondary_icon: {
    default: {
      base: {
        backgroundColor: tokenColors.secondaryIconButtonBackgroundDefault,
        borderColor: tokenColors.secondaryIconButtonOutlineDefault,
        borderWidth: 1.5,
        alignItems: 'center',
        justifyContent: 'center',
      },
      sizes: {
        default: {
          height: 48,
          width: 48,
        },
      },
      states: {
        active: {},
        hovered: {
          backgroundColor: tokenColors.secondaryIconButtonBackgroundHover,
        },
        pressed: {
          backgroundColor: tokenColors.secondaryIconButtonBackgroundPressed,
        },
        inactive: {
          backgroundColor: tokenColors.secondaryIconButtonBackgroundInactive,
        },
        loading: {
          backgroundColor: tokenColors.secondaryIconButtonBackgroundPressed,
        },
        selected: {
          backgroundColor: tokenColors.secondaryIconButtonBackgroundPressed,
        },
      },
      focused: {
        backgroundColor: tokenColors.secondaryIconButtonBackgroundFocus,
      },
    },
  },
  tertiary_icon: {
    default: {
      base: {
        color: tokenColors.tertiaryButtonTextActive,
      },
      sizes: {
        default: {
          padding: 14,
        },
        small: {
          padding: 8,
        },
      },
      states: {
        hovered: {
          backgroundColor: tokenColors.tertiaryButtonBackgroundHover,
        },
        pressed: {
          backgroundColor: tokenColors.tertiaryButtonBackgroundPressed,
        },
        inactive: {
          backgroundColor: tokenColors.tertiaryButtonBackgroundInactive,
        },
        loading: {
          backgroundColor: tokenColors.tertiaryButtonBackgroundPressed,
        },
      },
      focused: {
        backgroundColor: tokenColors.tertiaryButtonBackgroundFocus,
      },
    },
  },
  survey_icon: {
    default: {
      base: {
        backgroundColor: tokenColors.backgroundPrimary,
        borderColor: tokenColors.backgroundTransparent,
        borderWidth: 1.5,
        alignItems: 'center',
        justifyContent: 'center',
      },
      sizes: {
        default: {
          height: 48,
          width: 48,
        },
      },
      states: {
        active: {},
        hovered: {
          backgroundColor: tokenColors.secondaryButtonBackgroundHover,
          borderColor: tokenColors.secondaryButtonOutlineDefault,
        },
        pressed: {
          backgroundColor: tokenColors.secondaryButtonBackgroundPressed,
          borderColor: tokenColors.secondaryButtonOutlineDefault,
        },
        inactive: {
          backgroundColor: tokenColors.secondaryButtonBackgroundInactive,
          borderColor: tokenColors.secondaryButtonOutlineInactive,
        },
        loading: {
          backgroundColor: tokenColors.secondaryButtonBackgroundPressed,
        },
        selected: {
          backgroundColor: tokenColors.primaryButtonBackgroundDefault,
          color: tokenColors.primaryButtonTextActive,
        },
      },
      focused: {
        backgroundColor: tokenColors.secondaryButtonBackgroundHover,
        borderColor: tokenColors.secondaryButtonOutlineDefault,
      },
    },
  },
  secondary_tertiary_icon: {
    default: {
      base: {
        color: tokenColors.secondaryTertiaryButtonTextActive,
        backgroundColor: tokenColors.secondaryTertiaryButtonBackgroundDefault,
      },
      sizes: {
        default: {
          padding: 14,
        },
        small: {
          padding: 3,
        },
      },
      states: {
        hovered: {
          backgroundColor: tokenColors.secondaryTertiaryButtonBackgroundHover,
        },
        pressed: {
          backgroundColor: tokenColors.secondaryTertiaryButtonBackgroundHover,
        },
        inactive: {
          color: tokenColors.secondaryTertiaryButtonTextInactive,
          backgroundColor: tokenColors.secondaryTertiaryButtonBackgroundInactive,
        },
      },
      focused: {
        color: tokenColors.secondaryTertiaryButtonTextActive,
        backgroundColor: tokenColors.secondaryTertiaryButtonBackgroundFocus,
      },
    },
  },
  white_icon: {
    default: {
      base: {
        color: tokenColors.whiteIconButtonTextActive,
        backgroundColor: tokenColors.whiteIconButtonBackgroundDefault,
      },
      sizes: {
        default: {
          padding: 14,
        },
        small: {
          padding: 3,
        },
      },
      states: {
        hovered: {
          backgroundColor: tokenColors.whiteIconButtonBackgroundHover,
        },
        pressed: {
          backgroundColor: tokenColors.whiteIconButtonBackgroundHover,
        },
        inactive: {
          color: tokenColors.whiteIconButtonTextInactive,
          backgroundColor: tokenColors.whiteIconButtonBackgroundInactive,
        },
      },
      focused: {
        color: tokenColors.whiteIconButtonTextActive,
        backgroundColor: tokenColors.whiteIconButtonBackgroundFocus,
      },
    },
  },
  teal_icon: {
    default: {
      base: {
        backgroundColor: tokenColors.tealIconButtonBackgroundDefault,
        color: tokenColors.tealIconButtonIconDefault,
      },
      sizes: {
        default: {},
      },
      states: {
        hovered: {
          backgroundColor: tokenColors.tealIconButtonBackgroundHover,
          color: tokenColors.tealIconButtonIconHover,
        },
        pressed: {
          backgroundColor: tokenColors.tealIconButtonBackgroundPressed,
          color: tokenColors.tealIconButtonIconPressed,
        },
        selected: {
          backgroundColor: tokenColors.tealIconButtonBackgroundPressed,
          color: tokenColors.tealIconButtonIconPressed,
        },
      },
      focused: {
        backgroundColor: tokenColors.tealIconButtonBackgroundFocus,
        color: tokenColors.tealIconButtonIconFocus,
      },
    },
  },
  carousel: {
    default: {
      base: {
        color: tokenColors.iconDefault,
        backgroundColor: tokenColors.backgroundPrimary,
        borderColor: tokenColors.borderDefault,
      },
      sizes: {
        default: {},
      },
      states: {
        hovered: {
          backgroundColor: tokenColors.secondaryButtonBackgroundHover,
          borderColor: tokenColors.secondaryButtonOutlineDefault,
        },
        pressed: {
          backgroundColor: tokenColors.secondaryButtonBackgroundPressed,
          borderColor: tokenColors.secondaryButtonOutlineDefault,
        },
        inactive: {
          backgroundColor: tokenColors.secondaryButtonBackgroundInactive,
          borderColor: tokenColors.secondaryButtonOutlineInactive,
        },
        loading: {
          backgroundColor: tokenColors.secondaryButtonBackgroundPressed,
        },
        selected: {
          backgroundColor: tokenColors.primaryButtonBackgroundDefault,
          color: tokenColors.primaryButtonTextActive,
        },
      },
      focused: {
        backgroundColor: tokenColors.secondaryButtonBackgroundHover,
        borderColor: tokenColors.secondaryButtonOutlineDefault,
      },
    },
    filled: {
      base: {
        color: tokenColors.carouselButtonFilledIconActive,
        backgroundColor: tokenColors.carouselButtonFilledBackgroundActive,
      },
      sizes: {
        default: {},
      },
      states: {
        hovered: {
          backgroundColor: tokenColors.carouselButtonFilledBackgroundHover,
        },
        pressed: {
          backgroundColor: tokenColors.carouselButtonFilledBackgroundPressed,
        },
        inactive: {
          color: tokenColors.carouselButtonFilledIconInactive,
          backgroundColor: tokenColors.carouselButtonFilledBackgroundInactive,
        },
      },
      focused: {
        backgroundColor: tokenColors.carouselButtonFilledBackgroundFocus,
      },
    },
  },
  tertiary_header: {
    default: {
      base: {
        color: tokenColors.tertiaryButtonTextActive,
      },
      sizes: {
        default: {
          paddingTop: 8,
          paddingRight: 16,
          paddingBottom: 8,
          paddingLeft: 16,
        },
      },
      states: {
        hovered: {
          backgroundColor: tokenColors.tertiaryButtonBackgroundHover,
        },
        pressed: {
          backgroundColor: tokenColors.tertiaryButtonBackgroundPressed,
        },
        inactive: {
          color: tokenColors.tertiaryButtonTextInactive,
        },
        loading: {
          backgroundColor: tokenColors.tertiaryButtonBackgroundPressed,
        },
        selected: {
          backgroundColor: tokenColors.tertiaryButtonBackgroundPressed,
        },
      },
      focused: {
        backgroundColor: tokenColors.tertiaryButtonBackgroundFocus,
      },
    },
  },
})
